// src/utils/request.js
import axios from 'axios';
import router from '@/router';

const service = axios.create({
    baseURL: process.env.BASE_URL || '/api',
    timeout: 10000,
    // 401 走错误回调（有 token 刷新逻辑），其余走成功回调
    validateStatus: status => status !== 401
});

// 是否正在刷新 token
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(promise => {
        if (error) {
            promise.reject(error);
        } else {
            promise.resolve(token);
        }
    });
    failedQueue = [];
};

// 通用跳转登录方法
const redirectToLogin = () => {
    // 【安全修复】仅清除认证相关数据，保留其他业务缓存
    localStorage.removeItem('token');
    localStorage.removeItem('tokenLoginTime');
    localStorage.removeItem('permissions');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('customerInfo');
    localStorage.removeItem('menus');
    window.location.href = '/login';
};

// 请求拦截器
service.interceptors.request.use(async config => {
    // 跳过刷新 token 接口自己的拦截
    if (config.url === '/api/refreshToken') {
        return config;
    }

    const token = localStorage.getItem('token');

    if (token) {
        // 检查 token 是否需要刷新
        const loginTime = localStorage.getItem('tokenLoginTime');
        if (loginTime) {
            const elapsed = (Date.now() - parseInt(loginTime)) / 1000;
            const remaining = 86400 - elapsed; // 后端 token 有效期 1 天

            // 如果剩余时间少于 1 小时，尝试刷新
            if (remaining < 3600 && remaining > 0) {
                if (!isRefreshing) {
                    isRefreshing = true;
                    try {
                        const newToken = await refreshTokenInternal();
                        if (newToken) {
                            config.headers.Authorization = `Bearer ${newToken}`;
                            processQueue(null, newToken);
                        } else {
                            processQueue(new Error('refresh failed'), null);
                            redirectToLogin();
                            return Promise.reject(new Error('token expired'));
                        }
                    } catch (error) {
                        processQueue(error, null);
                        redirectToLogin();
                        return Promise.reject(error);
                    } finally {
                        isRefreshing = false;
                    }
                } else {
                    // 正在刷新中，加入队列等待
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    }).then(token => {
                        config.headers.Authorization = `Bearer ${token}`;
                        return config;
                    });
                }
            }
        }

        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
}, error => {
    return Promise.reject(error);
});

// 内部刷新 token 函数
const refreshTokenInternal = async () => {
    const oldToken = localStorage.getItem('token');
    if (!oldToken) return null;

    try {
        const response = await axios.post('/api/refreshToken', null, {
            headers: { 'Authorization': `Bearer ${oldToken}` }
        });

        const data = response.data;
        // 兼容两种响应格式：{code: 0, data: {token}} 或 {code: 200, data: {token}}
        if (data && (data.code === 0 || data.code === 200)) {
            const newToken = data.data?.token || data.token;
            if (newToken) {
                localStorage.setItem('token', newToken);
                localStorage.setItem('tokenLoginTime', Date.now().toString());
                return newToken;
            }
        }
        return null;
    } catch (error) {
        return null;
    }
};

// 响应拦截器
service.interceptors.response.use(
    response => {
        const status = response.status;

        // 处理 302 重定向
        if (status === 302) {
            const location = response.headers.location;
            if (location && location.includes('/login')) {
                redirectToLogin();
                return Promise.reject(new Error('session expired'));
            }
        }

        // 处理 5xx 服务器错误
        if (status >= 500) {
            return Promise.reject(new Error(`服务器异常(${status})，请稍后重试`));
        }

        // 检查业务状态码是否为 401（token 仍有效但业务层拒绝访问）
        const data = response.data;
        if (data && (data.code === 401 || data.code === 4010)) {
            redirectToLogin();
            return Promise.reject(new Error('token expired'));
        }

        return response;
    },
    async error => {
        const originalRequest = error.config;

        // 🆕 处理 error 中的 302
        if (error.response?.status === 302) {
            redirectToLogin();
            return Promise.reject(error);
        }

        // 跳过刷新 token 接口自己的重试
        if (originalRequest?.url === '/api/refreshToken') {
            redirectToLogin();
            return Promise.reject(error);
        }

        // 处理 401 错误
        if (error.response?.status === 401 && !originalRequest?._retry) {
            originalRequest._retry = true;

            if (!isRefreshing) {
                isRefreshing = true;
                try {
                    const newToken = await refreshTokenInternal();
                    if (newToken) {
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        processQueue(null, newToken);
                        return service(originalRequest);
                    } else {
                        processQueue(error, null);
                        redirectToLogin();
                        return Promise.reject(error);
                    }
                } catch (refreshError) {
                    processQueue(refreshError, null);
                    redirectToLogin();
                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            } else {
                // 正在刷新中，加入队列等待
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return service(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }
        }

        return Promise.reject(error);
    }
);

export default service;